import React from "react";
import styled from 'styled-components';
import ThemeTitleIcon from '@/common/components/ThemeTitleIcon';
import { Icon } from 'semantic-ui-react';
import { scoreToColor, SCORE_LOW_COLOR, SCORE_MEDIUM_COLOR, SCORE_HIGH_COLOR, SCORE_LOW_THRESHOLD, SCORE_MEDIUM_THRESHOLD } from '@/areas/dashboard/components/DashboardTeam.utils';

import { PillarData } from "../../../types/PillarData";
import { PILLAR_QUESTION_MAP } from '@/common/constants/pillars';
import { H2, H3, H5, PageSection, Title } from "@/common/styles/page";

const PillarDetailContainer = styled.div`
  width: 100%;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0.75rem 1.25rem 1.25rem;
  font-family: 'Poppins', sans-serif;

  @media (min-width: 1024px) {
    padding: 1rem 1.5rem 1.5rem;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(226, 232, 240, 1);
  padding-bottom: 1rem;
`;


const PillarOrgAvg = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const PillarDeltaText = styled.span<{ $status: 'positive' | 'neutral' | 'negative' }>`
  color: ${props =>
    props.$status === 'positive'
      ? '#047857'
      : props.$status === 'negative'
        ? '#991b1b'
        : '#6b7280'};

  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 1px;

`;

const SentimentPill = styled.div<{ $background: string; $color: string; $borderColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 6px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.02em;
  color: ${props => props.$color};
  background: ${props => `${props.$background}55`};
  border: 1px solid ${props => `${props.$borderColor}60`};
`;

const DetailContent = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: visible;
`;

const PillarThemesColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
`;

const PillarThemesColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-height: 0;
  margin-bottom: 0.75rem;
`;

const PillarThemesColumnHeader = styled.div<{ $color: string }>`
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.75rem;
  border-radius: 9999px;
  background: ${props => `${props.$color}15`};
  border: 1px solid ${props => `${props.$color}30`};
`;

const ThemeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 0 0.5rem 0;
  margin: 0 2.9rem;
`;

const ThemeCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ThemeStatusBar = styled.div<{ $color: string }>`
  width: 4px;
  min-height: 24px;
  background: ${props => props.$color};
  border-radius: 9999px;
  flex-shrink: 0;
`;

const ThemeCardTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  color: #111827;
  min-width: 0;
`;

const ThemeCardCount = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
  color: #6b7280;
`;

const ThemeCardDescription = styled.div`
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.5;
  margin-top: 0.25rem;
  margin-left: 1.2rem;
`;

const ThemeQuoteList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const ThemeQuote = styled.li`
  font-size: 0.8rem;
  color: #4b5563;
  font-style: italic;
`;


const ThemeTitle = styled(H2)`
  margin: 0;
  line-height: 1.1;
  font-size: 20px;
  font-weight: 450;
`;


type PillarDetailPaneProps = {
  selectedKey: string | null;
  role: string;
  pillars: PillarData[];
}

export const PillarDetailPane: React.FC<PillarDetailPaneProps> = ({ selectedKey, pillars }) => {
  const pillar = selectedKey ? pillars.find((p) => p.key === selectedKey) : undefined;

  const delta = pillar && pillar.org_avg > 0 ? Number((pillar.score ?? 0) - (pillar.org_avg ?? 0)) : null;
  const deltaSign = delta === null ? '' : delta > 0 ? '+' : delta < 0 ? '-' : '';
  const deltaDisplay = delta === null ? '' : delta > 0 ? `▲ ${Math.abs(delta).toFixed(1)}` : delta < 0 ? `▼ ${Math.abs(delta).toFixed(1)}` : `■ ${Math.abs(delta).toFixed(1)}`;

  const sentimentScoreValue = typeof pillar?.sentiment_score === 'number'
    ? Math.max(0, Math.min(10, pillar.sentiment_score))
    : Math.round(Math.max(0, Math.min(1, pillar?.qual_priority ?? 0)) * 10);

  const sentimentPill = (() => {
    if (sentimentScoreValue >= 8) return { label: 'Feedback was Very Positive', background: '#EEF6EF', color: '#1F6E3D', borderColor: '#D2E5D1' };
    if (sentimentScoreValue >= 6) return { label: 'Feedback was Positive', background: '#EAF0FF', color: '#1E40AF', borderColor: '#C8D7FF' };
    if (sentimentScoreValue >= 4) return { label: 'Feedback was Mixed', background: '#FEF5D6', color: '#7C4D1E', borderColor: '#F7E4A6' };
    if (sentimentScoreValue >= 2) return { label: 'Feedback was Mostly Negative', background: '#FEE2E2', color: '#B91C1C', borderColor: '#FECACA' };
    return { label: 'Feedback was Strong Need for Improvement', background: '#FEE2E2', color: '#B91C1C', borderColor: '#FECACA' };
  })();

  if (!pillar) {
    return (
      <PillarDetailContainer>
        <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
          Select a pillar on the left to view details.
        </div>
      </PillarDetailContainer>
    );
  }

  return (
    <PillarDetailContainer>
      <DetailHeader>
        <PageSection>
          <PageSection style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingBottom: '4px' }}>
            <Title padtop='0px'>{pillar.name}</Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flexWrap: 'wrap' }}>
              <H3>Team {pillar.score.toFixed(1)}</H3>
              {pillar.org_avg > 0 && (
                <>
                  <PillarDeltaText
                    $status={delta !== null ? (delta > 0 ? 'positive' : delta === 0 ? 'neutral' : 'negative') : 'neutral'}
                    title={`Team − Org: ${delta !== null ? `${deltaSign}${Math.abs(delta).toFixed(1)}` : ''}`}
                  >
                    {delta !== null ? deltaDisplay : ''}
                  </PillarDeltaText>
                  <H3>vs Org</H3>
                </>
              )}
            </div>
          </PageSection>

          <PageSection style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: 0 }}>
            <H5>{PILLAR_QUESTION_MAP[pillar.name] || ''}</H5>
            <div style={{ marginLeft: 'auto' }}>
              <SentimentPill
                $background={sentimentPill.background}
                $color={sentimentPill.color}
                $borderColor={sentimentPill.borderColor}
              >
                {sentimentPill.label}
              </SentimentPill>

            </div>
          </PageSection>
        </PageSection>
      </DetailHeader>

      <DetailContent>
        <div style={{ display: 'grid', gap: '1rem' }}>

          <div>
            {Array.isArray(pillar.themes) && pillar.themes.length > 0 ? (
              <PillarThemesColumns>
                {(() => {
                  const themeItems = [...pillar.themes]
                    .map((t) => {
                      const signalPct = typeof t.signal_strength === 'number'
                        ? Math.max(0, Math.min(100, t.signal_strength))
                        : Math.round(Math.max(0, Math.min(1, Number(t.sentiment ?? 0))) * 100);
                      const sentimentScore = signalPct / 10;
                      const statusColor = scoreToColor(sentimentScore);
                      const category = sentimentScore >= SCORE_MEDIUM_THRESHOLD
                        ? 'positive'
                        : sentimentScore >= SCORE_LOW_THRESHOLD
                          ? 'mixed'
                          : 'negative';

                      const desc = typeof t.description === 'string' ? t.description.trim() : '';
                      const quotes = Array.isArray((t as any).representative_quotes)
                        ? ((t as any).representative_quotes as string[])
                        : [];

                      return {
                        original: t,
                        themeTitle: t.label || 'Untitled theme',
                        themeDescription: desc,
                        count: typeof t.count === 'number' ? t.count : undefined,
                        sentimentScore,
                        statusColor,
                        category,
                        quotes,
                      };
                    })
                    .sort((a, b) => b.sentimentScore - a.sentimentScore);

                  const groupedThemes = {
                    positive: themeItems.filter((item) => item.category === 'positive'),
                    mixed: themeItems.filter((item) => item.category === 'mixed'),
                    negative: themeItems.filter((item) => item.category === 'negative'),
                  };

                  const columns = [
                    { title: 'Positive Feedback', key: 'positive', items: groupedThemes.positive, color: SCORE_HIGH_COLOR, icon: 'thumbs up' as const, variant: 'positive' as const },
                    { title: 'Mixed Feelings', key: 'mixed', items: groupedThemes.mixed, color: SCORE_MEDIUM_COLOR, icon: 'comment alternate' as const, variant: 'neutral' as const },
                    { title: 'Areas for Improvement', key: 'negative', items: groupedThemes.negative, color: SCORE_LOW_COLOR, icon: 'warning sign' as const, variant: 'negative' as const },
                  ];

                  return columns.map((column) => (
                    <PillarThemesColumn key={column.key}>
                      <PillarThemesColumnHeader $color={column.color}>
                        <ThemeTitleIcon variant={column.variant}>
                          <Icon name={column.icon} />
                        </ThemeTitleIcon>
                        <ThemeTitle>{column.title}</ThemeTitle>
                      </PillarThemesColumnHeader>
                      {column.items.length > 0 ? column.items.map((item) => (
                        <ThemeRow key={item.themeTitle} tabIndex={0}>
                          <ThemeCardHeader>
                            <ThemeStatusBar $color={item.statusColor} />
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <ThemeCardTitle>
                                {item.themeTitle}
                                {typeof item.count === 'number' ? (
                                  <ThemeCardCount>· {item.count} mentions</ThemeCardCount>
                                ) : null}
                              </ThemeCardTitle>
                            </div>
                          </ThemeCardHeader>
                          <ThemeCardDescription>
                            {item.themeDescription || <span style={{ color: '#9ca3af' }}>No description available</span>}
                          </ThemeCardDescription>
                          {item.quotes.length > 0 && (
                            <ThemeQuoteList>
                              {item.quotes.slice(0, 3).map((quote, idx) => (
                                <ThemeQuote key={idx}>&quot;{quote}&quot;</ThemeQuote>
                              ))}
                            </ThemeQuoteList>
                          )}
                        </ThemeRow>
                      )) : (
                        <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>No themes in this category.</div>
                      )}
                    </PillarThemesColumn>
                  ));
                })()}
              </PillarThemesColumns>
            ) : (
              <div style={{ color: '#6b7280' }}>No feedback themes available for this pillar.</div>
            )}
          </div>
        </div>
      </DetailContent>

    </PillarDetailContainer>
  );
}
